/**
 * Hook: User selection state (Set of ids) and handlers for Invite Users page.
 * Single Responsibility: which users are selected, toggle, select all.
 */

import { useState, useCallback } from 'react';

/**
 * Pure: toggle a user id in a Set (add if absent, remove if present).
 * @param {Set<string>} prev
 * @param {string} userId
 * @returns {Set<string>}
 */
export function toggleUserInSet(prev, userId) {
  const next = new Set(prev);
  if (next.has(userId)) next.delete(userId);
  else next.add(userId);
  return next;
}

export function useUserSelection() {
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());

  const handleToggleSelectUser = useCallback((userId) => {
    setSelectedUserIds((prev) => toggleUserInSet(prev, userId));
  }, []);

  const handleSelectAll = useCallback((userIds) => {
    setSelectedUserIds(new Set(userIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedUserIds(new Set());
  }, []);

  const removeFromSelection = useCallback((userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  }, []);

  return {
    selectedUserIds,
    setSelectedUserIds,
    handleToggleSelectUser,
    handleSelectAll,
    clearSelection,
    removeFromSelection,
  };
}
